import { type HttpContext } from '@angular/common/http';
import { Component, EventEmitter, inject, Injector, Input, OnInit, Output } from '@angular/core';
import { LocalStorageItem, LSBoolean } from '@frontendlib/common/decorators/local-storage.decorator';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { Stitch } from '@modules/common/types/stitch';
import { StitchFilters } from '@modules/common/types/stitch-filters';
import { ModalService } from '@modules/modal/services/modal.service';
import { Team } from '@modules/settings/types/team';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { applyInstanceDecorators, DeferInstanceDecorator } from '@sharedlib/common/lang/DeferInstanceDecorator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export abstract class BaseSidebarContainersItemComponent<
  C extends Stitch,
  I extends Stitch,
  Filters extends StitchFilters<C>,
  VirtualFolder extends string,
> implements OnInit
{
  @Input() team: Team;

  @Output() readonly selectFilter = new EventEmitter<VirtualFolder>();

  public abstract application: Application;
  public abstract sidebarSplitViewKey: SidebarSplitViewKey;

  public sidebarFilter: VirtualFolder;
  public minimized = true;
  public quickFormPopoverClose = new Subject<void>();
  @DeferInstanceDecorator(LSBoolean({ lsKey: 'sidebar-container.collapsed' }))
  declare public readonly expanded: LocalStorageItem<boolean>;
  public currentTeamId: string;

  protected filters: Filters;
  protected alive = new Subject<void>();

  private readonly splitViewService: SplitViewService = inject(SplitViewService);
  protected readonly stateService = inject<BaseAppStateService<C, I, VirtualFolder>>(BaseAppStateService);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);

  constructor() {
    applyInstanceDecorators(this, BaseSidebarContainersItemComponent);
  }

  ngOnInit(): void {
    this.splitViewService
      .getMinimized(this.sidebarSplitViewKey)
      .pipe(takeUntil(this.alive))
      .subscribe((minimized) => {
        this.minimized = minimized;
      });

    this.stateService
      .getVirtualFolder()
      .pipe(takeUntil(this.alive))
      .subscribe((sidebar) => {
        this.sidebarFilter = sidebar;
      });

    this.stateService
      .getTeam()
      .pipe(takeUntil(this.alive))
      .subscribe((teamId) => {
        this.currentTeamId = teamId;
      });
  }

  /**
   * "Add details" on the rail's quick form: carry what was typed into the full form, which opens on its
   * Details tab for a new container (see {@link ModalService.openFullForm}) rather than on a contents tab
   * that cannot have contents yet. Replaces the previous behaviour of navigating to the container in the
   * main view, which is not what the button says it does.
   *
   * Takes a `Stitch` rather than this rail's own `C`: the quick form's `more` output is typed
   * `EventEmitter<Stitch>` and knows nothing about which rail is hosting it, so the narrower signature
   * could not be bound. The full form dispatches on the item's own type, so nothing here needs `C`.
   */
  public handleMore(stitch: Stitch): void {
    this.modalService.openFullForm(stitch, this.injector, this.railRequestContext());
  }

  /**
   * The tenant this rail creates in, for "Add details" — the same context its quick form's `(save)`
   * creates with (#1055: the item carries its tenant; a NEW item has no `accountId` yet, so without
   * this the full form would fall back to whatever space is on screen). `undefined` = the current space.
   */
  protected railRequestContext(): HttpContext | undefined {
    return undefined;
  }
}
